import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import nso from './locales/nso.json';
import ts from './locales/ts.json';
import xi from './locales/xi.json';

/**
 * i18n bootstrap (web) — TD-072. Mirrors the mobile setup
 * (apps/mobile/src/i18n/index.ts) so the two apps share the same flat
 * dotted-key locale format and double-brace {{interpolation}} convention.
 *
 * Imported for side effects from main.tsx BEFORE the React tree renders, so the
 * first paint already has translations wired through the default i18next
 * instance (initReactI18next) — no explicit React provider needed.
 *
 * Locale resolution:
 *   1. User override persisted to localStorage under `fse_lang` (written by the
 *      Settings language picker — apps/web/src/routes/_app.settings.tsx).
 *   2. Fallback: en.
 *
 * The four pilot locales (en/xi/nso/ts) reflect the Limpopo pilot cohort. Only
 * en.json carries real English copy; xi/nso/ts hold the same English strings as
 * placeholders (TD-039) until human-SME translations land.
 */

export const SUPPORTED_LANGUAGES = ['en', 'xi', 'nso', 'ts'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Shared with the Settings picker (apps/web/src/routes/_app.settings.tsx) — keep
// the key in sync if either side changes.
const LANGUAGE_KEY = 'fse_lang';

function isSupported(code: string | null | undefined): code is SupportedLanguage {
  return typeof code === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(code);
}

/**
 * Read the persisted language choice. Guarded for SSR safety — `window` is
 * undefined when this module is imported outside the browser.
 */
function initialLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(LANGUAGE_KEY);
  return isSupported(stored) ? stored : 'en';
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    xi: { translation: xi },
    nso: { translation: nso },
    ts: { translation: ts },
  },
  lng: initialLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
  compatibilityJSON: 'v4',
});

export { i18n };
